import React, { memo, useEffect, useRef } from 'react';
import { ACTION_ICONS } from './constants';
import { EventsRegistry } from "../../../engine/events/index";
import "./TableCell.css"

const TableCell = memo(({ 
  isSelected,
  onClick,
  col, 
  item, 
  isSticky, stickyLeft, colWidth,
  index,
  unitIndex, 
  currentUnit, 
  updateContext, 
  triggerEvent, 
  handleRowBlur,
  tableId,
  isLocked,
}) => {
  // 1. Create a ref to the table cell (td)
  
  const cellRef = useRef(null);
  const rowId = item.row_id;
  

  const validateInput = (value, type, originalValue) => {
  // 1. NUMBER CHECK: Reject if user types a letter in a number column
  if (type === "number") {
    // Allows empty string (deleting), minus sign, and valid numbers
    const isValid = /^-?\d*\.?\d*$/.test(value);
    if (!isValid) return { valid: false };
  }

  // 2. Add other checks here (e.g., max length, specific patterns)
  
  return { valid: true, value };
};

  const colType = col.type; // e.g., "number", "text", "email"

  // Get the raw value from context to use as "Original" if needed
  const currentValue = item.row[col.name] ?? "";

  const handleChange = (e) => {
    const newValue = e.target.value;

    // --- HEALTH CHECK ---
    const check = validateInput(newValue, colType, currentValue);

    if (check.valid) {
      // Only update context if the character is "Healthy"
      updateContext(getPath(), newValue);
    } else {
      // REJECT: Don't call updateContext. 
      // The input will stay at its previous value automatically.
      console.warn(`%c🚫 Rejected invalid ${colType} input: ${newValue}`, "color: orange");
    }
  };
  const handleInputClick = (e) => {
    if (isSelected) {
      e.stopPropagation(); // Stops the selection from resetting when clicking text
    }
  };

  // 2. FOCUS LOGIC: When isSelected changes to true, find the input and focus it
  useEffect(() => {
    if (isSelected && cellRef.current) {
        setTimeout(() => {
          cellRef.current.scrollIntoView({
            behavior: 'auto',
            block: 'nearest', // Scrolls only if hidden
            inline: 'nearest'
          });

          // Now physically move the blinking cursor
          const input = cellRef.current.querySelector('input, select');
          if (input) {
            input.focus();
            if (input.tagName === 'INPUT') input.select();
          }
        }, 10);
    }
  }, [isSelected]); // This runs every time the pink border moves here

  const getPath = () => {
    const rowIndex = currentUnit.data.findIndex(r => r.row_id === rowId);
    return `context_units.${unitIndex}.data.${rowIndex}.row.${col.name}`;
  };

  let displayValue = item.row[col.name] ?? ""; 
  if (typeof displayValue === 'object' && displayValue !== null) {
    displayValue = displayValue.label || displayValue.value || JSON.stringify(displayValue);
  }

  const renderContent = () => {
    
    if (col.type === "sequence") return <span>{index + 1}</span>;
    // Inside TableCell.js render


    switch (col.type) {
      case "select":
        return (
          <select 
            className="cell-select"
            value={typeof item.row[col.name] === 'object' ? item.row[col.name].value : displayValue}
            
            onChange={(e) => updateContext(getPath(), e.target.value)}
            
          >
            {col.options?.map(opt => (
               <option key={opt.value || opt} value={opt.value || opt}>{opt.label || opt}</option>
            ))}
          </select>
        );
      case "buttonList":
        const buttonArray = Array.isArray(item.row[col.name]) ? item.row[col.name] : [];
        return (
          <div className="cell-btn-group">
            {buttonArray.map((btnText, bIdx) => {
              const actionKey = String(btnText).toLowerCase().trim();
              const config = ACTION_ICONS[actionKey] || ACTION_ICONS.default;
              return (
                <button 
                  key={`${rowId}-${actionKey}-${bIdx}`} 
                  className="cell-icon-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    triggerEvent(EventsRegistry.TABLE_ROW_ACTION, { 
                      unitId: tableId, rowId, action: actionKey, rowData: item.row
                    });
                  }}
                >
                  {config.icon}
                </button>
              );
            })}
          </div>
        );
        return null; 
      default:
        return (
          <input 
            className="cell-input"
            value={displayValue} 
            disabled={isLocked}
            onClick={handleInputClick} // Add this
            
            onChange={handleChange} // Use our new validated handler
            onBlur={() => handleRowBlur(rowId)}
          />
        );
    }
  };

  return (
    <td 
      ref={cellRef}
      className={`table-cell ${isSticky ? "sticky-col" : ""} ${isSelected ? 'selected' : ''}`} 
      onClick={(e) => onClick(rowId, col.name, e)}
      style={{ 
        position: isSticky ? 'sticky' : 'relative',
        left: isSticky ? `${stickyLeft}px` : 'auto',
        width: `${colWidth}px`,
        '--col-width': `${colWidth}px`,
        '--col-left': `${stickyLeft}px`
      }}
    >
      {renderContent()}
    </td>
  );
}, (prev, next) => {
  // IMPORTANT: Re-render if isSelected changes, otherwise the focus effect won't run!
  return (
    prev.item.row[prev.col.name] === next.item.row[next.col.name] && 
    prev.isSelected === next.isSelected && 
    prev.index === next.index
  );
});

export default TableCell;
